<script setup lang="ts">
import { ref, onMounted, watch, computed, Ref } from 'vue'
import { AxiosResponse } from 'axios'
import { useI18n } from 'vue-i18n'
import countries from '@/shared/countries.json'
import { useSubscriptionStore } from '@/store/subscription/subscriptionStore'
import createHttpClient from '@/api/httpClient'
import MessageService from '@/components/feedback/message/messageService'
import { useUserTvaStore } from '@/store/tva/userTvaStore'
import ChangeSubscription from '@/components/ChangeSubscription.vue'
import { SubscriptionData } from '@/shared/subscription'
import { InformationData } from '@/api/account/information.d'
import { useUserStore } from '@/store/user/userStore'
import {
  Package,
  Type,
  ChangeSubscriptionData,
  PaymentResponse,
} from '@/api/subscription-packages/package.d'
import { TvaNumberRegex } from '@/utils/regex'
import AddonsList from '@/components/subscription-packages/AddonsList.vue'
import PackagesCard from '@/components/subscription-packages/PackagesCard.vue'

import PaymentField from '@/components/inputs/PaymentField.vue'
import Address from '@/components/inputs/Address.vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'

import { DiscountCodeDetailsResponse } from '@/api/subscription-packages/package.d'

const paymentRef = ref(null)
const subscriptionStore = useSubscriptionStore()
const httpClient = createHttpClient()
const globalErrors = ref()
const information = ref<InformationData>()
const userStore = useUserStore()

const router = useRouter()
const { t } = useI18n()
const emit = defineEmits(['update:company', 'update:close'])

const requiredRules = [v => !!v || `${t('dashboard.subscription.required')}`]
const loading = ref(false)
const userTva = useUserTvaStore()
const useAlreadySave = ref(true)
const becomeContributor = ref(false)

const haveDiscountCode = ref(false)

const codeMessage = ref('')

const discountDetail = ref<DiscountCodeDetailsResponse>()
const discountedPrice = ref(0)

const code = ref('')

const codeVerify = ref(false)

const currentChanging = computed(() => subscriptionStore.currentChangingPlan)

const tickLabels = ref({
  10: '1',
  20: '2',
  30: '3',
  40: '4',
  50: '5',
  60: '10',
  70: '20',
  80: '30',
  90: '40',
  100: '50',
  110: '100',
})

const contributionValue = ref(0)

// Gestion de la saisie manuelle
const handleManualInput = () => {
  const numericValue = Number(changingData.value.contributionAmount)

  if (!isNaN(numericValue)) {
    // Si la valeur existe dans tickLabels, synchroniser avec le slider
    const foundKey = Object.keys(tickLabels.value).find(
      key => tickLabels.value[key] === changingData.value.contributionAmount,
    )

    if (foundKey) {
      contributionValue.value = Number(foundKey)
    }
  }
}

const isValidForm = ref(false)
const isInitializing = ref(false)
const choice = ref('')
const displayPriceWithTva = ref(false)
const loadingPrice = ref(false)
const getValidType = computed(() => {
  return subscriptionStore.getSubscriptionType()
})

const redirectSuccess = () => {
  MessageService.success(t('dashboard.subscription.change_with_success'))
  loading.value = false
  subscriptionStore.toggleSubscription()
  userStore.refreshToken()
  router.push({
    name: 'DashboardSubscription',
  })
}

const changingData: Ref<ChangeSubscriptionData> = ref({
  newSubscriptionLevelId: currentChanging.value.id,
  isContributor: false,
  contributionAmount: 0,
  couponCode: null,
  subscriptionAddons: subscriptionStore.selectedAddons,
})

// checking discount code and getting details
const discountCodeDetail = async () => {
  codeMessage.value = ''
  discountDetail.value = null

  if (codeVerify.value) return false

  if (!changingData.value.couponCode) {
    codeMessage.value = t('register.step2.enter_discount_code')
    return false
  }
  codeVerify.value = true

  try {
    const res: AxiosResponse<DiscountCodeDetailsResponse> =
      await httpClient.post<DiscountCodeDetailsResponse>(
        '/discount-code/details',
        { code: changingData.value.couponCode },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
    discountDetail.value = res.data

    if (discountDetail.value.id) {
      codeMessage.value = t('register.step2.valid_discount_code')
    }

    if (discountDetail.value.type === 'fixed') {
      discountedPrice.value = (discountDetail.value.amount_off / 100) as number
    } else {
      discountedPrice.value =
        Number(currentChanging.value.price) *
        (discountDetail.value.percent_off / 100)
    }
  } catch (_) {
    codeMessage.value = t('register.step2.invalid_discount_code')
  } finally {
    codeVerify.value = false
  }
}

const onSubmit = async () => {
  globalErrors.value = ''

  changingData.value.subscriptionAddons =
    subscriptionStore.selectedAddons.filter(
      addon => addon.lifeAlreadyPaid !== true,
    )

  try {
    loading.value = true

    if (displayPaymentDetail.value) {
      if (useAlreadySave.value) {
        // CB déja enrégistré
        if (subscriptionStore.isCardExpired) {
          // check expired CB
          globalErrors.value = t('expired-card')
          loading.value = false
          return false
        }
        await subscriptionStore
          .changeSubscriptionPlan(changingData.value)
          .then(async (result: PaymentResponse) => {
            if (result) {
              await paymentRef.value
                .confirmCardPayment(result.clientSecret, result.paymentMethodId)
                .then(async () => {
                  const res = await subscriptionStore.finalizeChanges({
                    subscriptionLevelId: currentChanging.value.id,
                    paymentIntentId: result.paymentIntentId,
                    subscriptionAddons: changingData.value.subscriptionAddons,
                  })

                  if (res.status === 'succeeded' || res.status === 'success') {
                    loading.value = true
                  }

                  // 🔁 Nouveau bloc : encore une confirmation nécessaire ?
                  if (
                    (res.status === 'requires_action' ||
                      res.status === 'requires_confirmation') &&
                    res.clientSecret
                  ) {
                    await paymentRef.value
                      .confirmCardPayment(
                        res.clientSecret,
                        result.paymentMethodId,
                      )
                      .then(async () => {
                        // loading.value = false
                      })
                      .catch(e => {
                        console.log(e)
                        loading.value = false
                      })
                  } else {
                  }
                })
                .catch(() => {
                  loading.value = false
                })

              await subscriptionStore
                .confirmSubscription({
                  subscriptionLevelId: currentChanging.value.id,
                  paymentIntentId: result.paymentIntentId,
                  subscriptionAddons: changingData.value.subscriptionAddons,
                })
                .then(confirmRes => {
                  if (
                    confirmRes.status === 'succeeded' ||
                    confirmRes.status === 'success'
                  ) {
                    redirectSuccess()
                    return
                  }
                })
                .catch(() => {
                  loading.value = false
                })
            }
            // }
          })
        // loading.value = false
      } else {
        await create_pm()
      }
    } else {
      await subscriptionStore
        .changeSubscriptionPlan(changingData.value)
        .then(async (result: PaymentResponse) => {
          if (result) {
            await paymentRef.value
              .confirmCardPayment(result.clientSecret, result.paymentMethodId)
              .then(async () => {
                const res = await subscriptionStore.finalizeChanges({
                  subscriptionLevelId: currentChanging.value.id,
                  paymentIntentId: result.paymentIntentId,
                  subscriptionAddons: changingData.value.subscriptionAddons,
                })

                if (res.status === 'succeeded' || res.status === 'success') {
                  loading.value = true
                }

                // 🔁 Nouveau bloc : encore une confirmation nécessaire ?
                if (
                  (res.status === 'requires_action' ||
                    res.status === 'requires_confirmation') &&
                  res.clientSecret
                ) {
                  await paymentRef.value
                    .confirmCardPayment(
                      res.clientSecret,
                      result.paymentMethodId,
                    )
                    .then(async () => {
                      // loading.value = false
                    })
                    .catch(e => {
                      console.log(e)
                      loading.value = false
                    })
                } else {
                }
              })
              .catch(() => {
                loading.value = false
              })

            await subscriptionStore
              .confirmSubscription({
                subscriptionLevelId: currentChanging.value.id,
                paymentIntentId: result.paymentIntentId,
                subscriptionAddons: changingData.value.subscriptionAddons,
              })
              .then(confirmRes => {
                if (
                  confirmRes.status === 'succeeded' ||
                  confirmRes.status === 'success'
                ) {
                  redirectSuccess()
                  return
                }
              })
              .catch(() => {
                loading.value = false
              })
          }
          // }
        })
    }
  } catch (error) {
    console.error(error)
    loading.value = false
  }
}

// save new user Card
const create_pm = async () => {
  const res = await paymentRef.value.paymentMethod()
  if (res.status) {
    const result: any = await subscriptionStore.updateUserCard(
      res.paymentMethodId,
    )
    if (result.status) {
      paymentRef.value
        .confirmCardSetup(result.clientSecret, res.paymentMethodId) // stocker la CB pour une utilisation futur
        .then(async response => {
          if (response.status) {
            subscriptionStore
              .changeSubscriptionPlan(changingData.value) // confirmer la changement de formule
              .then(async (result: PaymentResponse) => {
                if (result) {
                  await paymentRef.value
                    .confirmCardPayment(
                      result.clientSecret,
                      result.paymentMethodId,
                    )
                    .then(async () => {
                      const res = await subscriptionStore.finalizeChanges({
                        subscriptionLevelId: currentChanging.value.id,
                        paymentIntentId: result.paymentIntentId,
                        subscriptionAddons:
                          changingData.value.subscriptionAddons,
                      })

                      if (
                        res.status === 'succeeded' ||
                        res.status === 'success'
                      ) {
                        loading.value = true
                      }

                      // 🔁 Nouveau bloc : encore une confirmation nécessaire ?
                      if (
                        (res.status === 'requires_action' ||
                          res.status === 'requires_confirmation') &&
                        res.clientSecret
                      ) {
                        await paymentRef.value
                          .confirmCardPayment(
                            res.clientSecret,
                            result.paymentMethodId,
                          )
                          .then(async () => {
                            // loading.value = false
                          })
                          .catch(e => {
                            console.log(e)
                            loading.value = false
                          })
                      } else {
                      }
                    })
                    .catch(() => {
                      loading.value = false
                    })

                  await subscriptionStore
                    .confirmSubscription({
                      subscriptionLevelId: currentChanging.value.id,
                      paymentIntentId: result.paymentIntentId,
                      subscriptionAddons: changingData.value.subscriptionAddons,
                    })
                    .then(confirmRes => {
                      if (
                        confirmRes.status === 'succeeded' ||
                        confirmRes.status === 'success'
                      ) {
                        redirectSuccess()
                        return
                      }
                    })
                    .catch(() => {
                      loading.value = false
                    })
                }
              })
              .catch(_ => {
                loading.value = false
                MessageService.error('')
              })
          } else {
            globalErrors.value = response.error
            loading.value = false
            MessageService.error('')
          }
        })
    } else {
      loading.value = false
      globalErrors.value = result.error
    }
  } else {
    globalErrors.value = res.error
    loading.value = false
    return false
  }
}

const tva = ref(0)

watch(
  () => subscriptionStore.changeSubscription,
  () => {
    isInitializing.value = true
    if (
      subscriptionStore.cardData &&
      subscriptionStore.cardData.lastFour &&
      !subscriptionStore.isCardExpired
    ) {
      useAlreadySave.value = true
    } else {
      useAlreadySave.value = false
    }

    setTimeout(() => {
      isInitializing.value = false
    }, 0)
  },
  { immediate: true },
)

watch(
  () => subscriptionStore.paymentErrors,
  () => {
    if (subscriptionStore.paymentErrors) {
      globalErrors.value = subscriptionStore.paymentErrors
      loading.value = false
    }
  },
  { immediate: true },
)

const the_price = ref(null)
const the_tva = ref(null)

onMounted(() => {
  if (!subscriptionStore.userInformation) return
  information.value = subscriptionStore.userInformation

  tva.value = subscriptionStore.subscriptionStatus['tva']

  the_price.value = subscriptionStore.subscriptionStatus['actualPrice']
  the_tva.value = subscriptionStore.subscriptionStatus['tva']

  // changingData.value.subscriptionAddons = subscriptionStore.selectedAddons

  if (tva.value > 0) {
    displayPriceWithTva.value = true
    const tvaPrice =
      subscriptionStore.currentChangingPlan.price * (1 + tva.value / 100)

    subscriptionStore.currentChangingPlan = {
      ...subscriptionStore.currentChangingPlan,
      priceWithTva: tvaPrice,
    }
  }
})

const displayCurrentSubscriptionBtn = computed(() => {
  const currentSubscription = getValidType.value

  if (currentSubscription === subscriptionStore.changingPlan.id) {
    return true
  }

  return false
})

const loadTva = computed(() => {
  return userTva.loadTva
})

const closeChangeSubscription = async () => {
  if (loading.value) return false
  subscriptionStore.toggleSubscription()
  // await subscriptionStore.fetchSubscriptionStatus()
}

const formatCardNumber = computed(() => {
  return `**** **** **** ${subscriptionStore.cardData.lastFour}`
})

const formatExpired = computed(() => {
  return `${String(subscriptionStore.cardData.expiryMonth).padStart(2, '0')} / ${subscriptionStore.cardData.expiryYear}`
})

const displayPaymentDetail = computed(() => {
  if (subscriptionStore.trialRemainingDays > 0) {
    // en période d'essai
    if (choice.value === 'lifetime') {
      return true
    } else {
      return false
    }
  } else {
    return true
  }
})

const disabledWhenChanging = computed(() => {
  if (
    subscriptionStore.subscriptionStatus['status'] === 'canceled' ||
    subscriptionStore.subscriptionStatus['status'] === 'incomplete_expired' ||
    subscriptionStore.subscriptionStatus['status'] === 'unpaid'
  ) {
    return false
  }

  if (
    subscriptionStore.subscriptionStatus['status'] !== 'active' &&
    subscriptionStore.subscriptionStatus['type'] === 'monthly' &&
    subscriptionStore.subscriptionStatus['monthsAlreadyPaid'] <= 40
  ) {
    if (subscriptionStore.getSubscriptionType() === choice.value) {
      return true
    }
  }

  return false
})

function getMatchingAddon(addon: { id: string; billing: string }) {
  return currentChanging.value.addons.find(
    selected => selected.id === addon.id && selected.billing === addon.billing,
  )
}

const addonsTotal = computed(() => {
  return subscriptionStore.selectedAddons.reduce((total, selectedAddon) => {
    const matchingAddon = currentChanging.value?.addons.find(
      addon =>
        addon.id === selectedAddon.id &&
        addon.billing === selectedAddon.billing &&
        selectedAddon?.lifeAlreadyPaid !== true,
    )

    if (!matchingAddon) return total

    const price = matchingAddon.price ?? 0

    return total + Number(price)
  }, 0)
})

const packWithAddonsTotal = computed(() => {
  const result =
    Number(currentChanging.value.price || 0) + Number(addonsTotal.value || 0)
  if (result > 0) {
    return result
  }

  return 0
})

const totalHT = computed(() => {
  if (subscriptionStore.subscriptionStatus['type']?.includes('life')) {
    currentChanging.value.price = 0
  }
  const result =
    Number(currentChanging.value.price || 0) +
    Number(addonsTotal.value || 0) +
    Number(changingData.value.contributionAmount || 0) -
    Number(discountedPrice.value || 0)
  if (result > 0) {
    return result
  }

  return 0
})

const vatAmount = computed<number>(() => {
  const vat =
    totalHT.value * (subscriptionStore.subscriptionStatus['tva'] / 100)
  return Math.round(vat * 100) / 100
})

// const billingTotal = computed(() => {
//   return (
//     currentChanging.value.price +
//     vatAmount.value +
//     Number(changingData.value.contributionAmount)
//   )
// })

const billingTotal = computed(() => {
  const result =
    Number(currentChanging.value.price || 0) +
    Number(addonsTotal.value || 0) +
    Number(vatAmount.value || 0) +
    Number(changingData.value.contributionAmount || 0) -
    Number(discountedPrice.value || 0)
  if (result > 0) {
    return result
  }

  return 0
})

watch(
  () => changingData.value.couponCode,
  () => {
    discountedPrice.value = 0
    codeMessage.value = ''
  },
)

watch(
  contributionValue,
  newValue =>
    (changingData.value.contributionAmount =
      tickLabels.value[newValue] || newValue.toString()),
)
</script>

<template>
  <v-dialog
    :model-value="subscriptionStore.changeSubscription"
    persistent
    max-width="700"
    transition="dialog-top-transition"
  >
    <v-card color="white" rounded="xl" :disabled="loading">
      <v-card-title
        class="py-3 d-flex justify-center w-full align-center bg-surface-lighten-1"
      >
        <span class="font-weight-bold title">{{
          subscriptionStore.subscriptionStatus['status'] === 'canceled'
            ? t('dashboard.subscription.active_btn')
            : t('dashboard.subscription.change_subscription_title')
        }}</span>
        <img
          style="position: absolute; right: 10px"
          class="cursor-pointer mr-2"
          @click="closeChangeSubscription"
          src="@/assets/_close.svg"
        />
      </v-card-title>

      <v-card-text>
        <v-form
          @submit.prevent="onSubmit"
          @keydown.enter.prevent=""
          v-model="isValidForm"
          class="d-flex flex-column ga-5"
        >
          <div class="mb-3">
            <v-row dense>
              <!-- current change plan  -->
              <v-col cols="12" sm="6">
                <PackagesCard
                  style="max-width: 350px"
                  :classes="'border-md border-secondary border-opacity-100 rounded-xl pa-2'"
                  :displayAction="false"
                  :showFeature="false"
                  :thePackage="currentChanging"
                  :loadingPrice="loadingPrice"
                />
              </v-col>
              <!-- recap plan  -->
              <v-col cols="12" sm="6">
                <v-card
                  elevation="0"
                  color="#F9F9F9"
                  rounded="xl"
                  class="border-sm border-ligth-gray border-opacity-100 fill-height pa-5 d-flex flex-column"
                >
                  <h3 class="recap-title">
                    {{ $t('register.step2.recap_title') }}
                  </h3>

                  <div class="d-flex flex-column">
                    <div class="d-flex flex-column">
                      <div class="d-flex justify-space-between item-line">
                        <span
                          class="title"
                          v-if="!currentChanging?.type?.includes('life')"
                          >{{
                            $t('register.step2.pack_quantity', {
                              value:
                                currentChanging.type === 'annually'
                                  ? 'an'
                                  : 'mois',
                            })
                          }}</span
                        >
                        <span class="title" v-else>{{
                          $t('register.step2.pack_quantity', {
                            value: ' à vie',
                          })
                        }}</span>
                        <span class="value">{{ currentChanging.price }}€</span>
                      </div>
                      <v-divider />

                      <!-- Addons List Price Recap -->
                      <div
                        class="d-flex flex-column ga-4 item-line"
                        v-if="subscriptionStore.selectedAddons.length > 0"
                      >
                        <div class="d-flex justify-space-between">
                          <span class="title"> Options </span>
                          <span class="value"> {{ addonsTotal }} € </span>
                        </div>
                        <v-divider />
                      </div>

                      <div
                        class="d-flex flex-column ga-4"
                        v-if="
                          discountDetail &&
                          discountDetail.id &&
                          discountedPrice > 0
                        "
                      >
                        <div class="d-flex justify-space-between item-line">
                          <span class="title">
                            {{
                              $t('register.step2.discount_title', {
                                discount:
                                  discountDetail.type === 'percentage'
                                    ? `${discountDetail.percent_off}%`
                                    : '',
                              })
                            }}</span
                          >
                          <span class="plan-price">
                            {{
                              discountedPrice > 0
                                ? `-${discountedPrice}`
                                : discountedPrice
                            }}
                            €</span
                          >
                        </div>
                        <v-divider />
                      </div>

                      <template v-if="currentChanging.category === 'premium'">
                        <div class="d-flex justify-space-between item-line">
                          <span class="title">{{
                            $t('register.step2.contribution')
                          }}</span>
                          <span class="contribution-value"
                            >{{ changingData.contributionAmount }}€</span
                          >
                        </div>
                        <v-divider />
                      </template>

                      <div class="d-flex justify-space-between item-line">
                        <span class="title">{{
                          $t('register.step2.taxes')
                        }}</span>
                        <span class="value"> {{ vatAmount }}€</span>
                      </div>
                      <v-divider />
                    </div>

                    <div class="d-flex justify-space-between pt-5">
                      <span class="total"
                        >{{
                          $t('register.step2.total', {
                            value: vatAmount > 0 ? 'TTC' : 'HT',
                          })
                        }}
                      </span>
                      <span class="total-value">{{ billingTotal }}€</span>
                    </div>

                    <span
                      class="cursor-pointer have-discount-label become-title"
                      @click="haveDiscountCode = !haveDiscountCode"
                      >{{ $t('register.step2.have_discount_code') }}</span
                    >

                    <div v-if="haveDiscountCode">
                      <label class="discount-label">{{
                        $t('register.step2.enter_discount_code')
                      }}</label>
                      <div class="d-flex align-center ga-2">
                        <v-text-field
                          clearable
                          @keyup.enter="() => discountCodeDetail()"
                          :disabled="codeVerify"
                          v-model="changingData.couponCode"
                          rounded="lg"
                          density="compact"
                          class=""
                          hide-details
                        />
                        <v-btn
                          @click="() => discountCodeDetail()"
                          :loading="codeVerify"
                          round="lg"
                          color="secondary"
                          >{{ $t('register.step2.valide_btn') }}</v-btn
                        >
                      </div>
                      <div
                        v-if="codeMessage"
                        class="code-status-res d-flex align-center ga-1"
                        :class="
                          discountDetail && discountDetail.id
                            ? 'text-teal-green'
                            : 'text-red'
                        "
                      >
                        {{ codeMessage }}
                        <CheckednIcon
                          v-if="discountDetail && discountDetail.id"
                        />
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Liste des addons liés au plan actuel -->
          <AddonsList
            :package="subscriptionStore.currentChangingPlan"
            :billing="subscriptionStore.currentChangingPlan.type"
            :showCheckboxes="false"
            :displayPriceWithTva="displayPriceWithTva"
            :notDisplayLifeAddonsAlreadyPaid="true"
          />

          <template v-if="currentChanging.category === 'premium'">
            <div
              class="border rounded-lg d-flex flex-column"
              style="padding: 10px"
            >
              <div class="d-flex justify-space-between align-center">
                <h3 class="become-title">
                  {{ $t('register.step2.become_contributor') }}
                </h3>
                <v-switch
                  :true-value="true"
                  :false-value="false"
                  v-model="changingData.isContributor"
                  hide-details
                  base-color="#9CA1B9"
                  density="compact"
                  color="secondary"
                />
              </div>
              <p class="contribution-detail">
                {{ $t('register.step2.become_contribution_detail') }}
              </p>
            </div>

            <div
              class="border pa-4 rounded-lg d-flex flex-column ga-2"
              v-if="changingData.isContributor"
            >
              <span class="become-title">Définissez un montant</span>
              <v-row dense>
                <v-col cols="12" sm="10">
                  <v-slider
                    tile
                    :max="115"
                    color="#12C29B"
                    density="compact"
                    show-ticks="always"
                    :ticks="tickLabels"
                    step="10"
                    tick-size="5"
                    hide-details
                    track-color="#E0E0E0"
                    v-model="contributionValue"
                  ></v-slider>
                </v-col>

                <v-col cols="12" sm="2">
                  <v-text-field
                    density="compact"
                    v-model="changingData.contributionAmount"
                    @input="handleManualInput"
                    rounded="lg"
                    hide-details
                    class=""
                    type="number"
                    min="1"
                    suffix="€"
                  />
                </v-col>
              </v-row>
              <p class="contribution-role-desc">
                Ce montant sera ajouté automatiquement à votre forfait premuim
                par an
              </p>
            </div>
          </template>

          <div class="">
            <div class="border pa-4 rounded-xl">
              <div class="billing-info-title">
                {{ t('dashboard.subscription.billing_info') }}
              </div>

              <div v-if="subscriptionStore.cardData.lastFour" class="mb-3">
                <v-radio-group
                  class="d-flex"
                  inline
                  hide-details
                  v-model="useAlreadySave"
                >
                  <v-radio
                    color="secondary"
                    :readonly="loading"
                    :label="$t('use_already_save')"
                    :value="true"
                    class="text-rigth"
                  ></v-radio>

                  <v-radio
                    color="secondary"
                    :readonly="loading"
                    :label="$t('new_card')"
                    :value="false"
                  ></v-radio>
                </v-radio-group>
              </div>

              <div
                v-if="
                  subscriptionStore.cardData &&
                  subscriptionStore.cardData.lastFour &&
                  useAlreadySave
                "
                class="d-sm-flex ga-sm-2"
              >
                <div class="w-sm-50 mb-sm-0 mb-2">
                  <label>{{ t('register.card_number') }}</label>
                  <p class="text-disabled">{{ formatCardNumber }}</p>
                </div>

                <div class="w-sm-50">
                  <label>{{ t('expired') }}</label>
                  <p class="text-disabled">{{ formatExpired }}</p>
                </div>
              </div>

              <PaymentField ref="paymentRef" v-show="!useAlreadySave" />
            </div>

            <div class="text-red text-caption text-center mt-2">
              {{ globalErrors }}
            </div>
          </div>

          <div class="d-flex justify-center">
            <v-btn
              :loading="loading"
              @keyup.enter.prevent=""
              type="submit"
              color="white"
              class="bg-secondary px-5 mt-5 w-sm-75 mx-auto"
              >{{
                subscriptionStore.subscriptionStatus['status'] === 'canceled'
                  ? t('dashboard.subscription.active_btn')
                  : t('dashboard.subscription.change_subscription_btn')
              }}</v-btn
            >
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.title {
  color: #007058;
  font-weight: 600px;
  font-size: 20px;
  line-height: 32.2px;
}

.card-body {
  width: 389px;
  margin: auto;
}

.no-focus:focus {
  outline: none;
  box-shadow: none;
}
.border-custom {
  border-right: 1px solid #ccc;
}

.card-body {
  width: 416px;
  margin: auto;
}

.billing-info-title {
  color: #616161;
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  letter-spacing: 0;
}

.current_subscription_btn {
  font-size: 13px;
}

label {
  font-size: 14px;
}

.item-line {
  padding-top: 14px;
  padding-bottom: 14px;
}

.item-line .title {
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.28;
}

.item-line .value {
  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.28;
}

.item-line .contribution-value {
  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
  color: #d6b25d;
  letter-spacing: -0.28;
}

.total {
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  color: #007058;
  letter-spacing: -0.64;
}

.total-value {
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  color: #029978;
  letter-spacing: -0.64;
}

.recap-title {
  color: #029978;
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  letter-spacing: -0.64;
}

.pack-price {
  font-size: 30px !important;
}

.contribution-detail {
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.24;
}

.become-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.64;
}

.contribution-role-desc {
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.24;
  margin-top: 10px;
}

.code-status-res {
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.48;
}

.have-discount-label {
  color: #12c29b;
  font-weight: 600;
  margin-top: 14px;
  font-size: 14px;
  line-height: 140%;
}

.discount-label {
  color: #616161;
  margin-top: 14px;
  font-size: 14px;
  line-height: 140%;
}

@media (max-width: 599px) {
  .card-body {
    width: 100%;
    margin: auto;
  }

  .border-custom {
    border-right: none;
    border-bottom: 1px solid #ccc;
  }
}
</style>
